import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, KeyRound, Plus, XCircle } from "lucide-react";
import {
  Button, Select, Label, Modal, ModalFooter, Badge, EmptyState, Skeleton,
  useToast, useConfirm,
} from "@/components/ui";
import { ApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { storeApi, type StoreCustomer, type StoreEntitlement, type StoreProduct } from "./api";

export function StoreCustomersPage() {
  const toast = useToast();
  const [customers, setCustomers] = useState<StoreCustomer[] | null>(null);
  const [open, setOpen] = useState<StoreCustomer | null>(null);

  const load = useCallback(() => {
    storeApi.customers()
      .then((r) => setCustomers(r.customers))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed to load customers"));
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-zinc-100">Customers</h1>
        <p className="mt-1 text-sm text-zinc-400">
          Everyone with a store account. They're also in your Contacts — this view adds their access grants.
        </p>
      </div>

      {customers === null ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : customers.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No customers yet"
          description="Shoppers appear here when they create an account or buy something on your store."
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">Customer</th>
                <th className="px-4 py-2.5 font-medium">Orders</th>
                <th className="px-4 py-2.5 font-medium">Total spent</th>
                <th className="hidden px-4 py-2.5 font-medium sm:table-cell">Access grants</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/70">
              {customers.map((c) => (
                <tr key={c.user_id} className="cursor-pointer bg-zinc-950 transition hover:bg-zinc-900/50" onClick={() => setOpen(c)}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-100">{c.name || c.email}</div>
                    {c.name && <div className="text-xs text-zinc-500">{c.email}</div>}
                  </td>
                  <td className="px-4 py-3 text-zinc-400">{c.orders}</td>
                  <td className="px-4 py-3 text-zinc-200">{formatMoney(c.spent_cents)}</td>
                  <td className="hidden px-4 py-3 text-zinc-400 sm:table-cell">{c.entitlements}</td>
                  <td className="px-4 py-3 text-right">
                    {c.contact_id && (
                      <Link
                        to={`/contacts/${c.contact_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-rose-400 hover:underline"
                      >
                        Contact →
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <CustomerDetail customer={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}

function CustomerDetail({ customer, onClose, onChanged }: {
  customer: StoreCustomer;
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [entitlements, setEntitlements] = useState<StoreEntitlement[] | null>(null);
  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [grantId, setGrantId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    storeApi.customerEntitlements(customer.user_id)
      .then((r) => setEntitlements(r.entitlements))
      .catch(() => setEntitlements([]));
    storeApi.products().then((r) => setProducts(r.products)).catch(() => setProducts([]));
  }, [customer.user_id]);
  useEffect(() => { load(); }, [load]);

  const grant = async () => {
    if (!grantId) return;
    setBusy(true);
    try {
      await storeApi.grant(customer.user_id, grantId);
      toast.success("Access granted");
      setGrantId("");
      load();
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (e: StoreEntitlement) => {
    if (!(await confirm({ title: `Revoke access to "${e.product_name}"?`, confirmLabel: "Revoke", danger: true }))) return;
    try {
      await storeApi.revoke(e.id);
      toast.success("Revoked");
      load();
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    }
  };

  const active = (entitlements ?? []).filter((e) => e.status === "active");
  const grantable = products.filter((p) => !active.some((e) => e.product_id === p.id));

  return (
    <Modal open onClose={onClose} title={customer.name || customer.email || "Customer"} size="md">
      <div className="max-h-[70vh] space-y-4 overflow-y-auto p-5">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
            <div className="text-lg font-semibold text-zinc-100">{customer.orders}</div>
            <div className="text-xs text-zinc-500">Orders</div>
          </div>
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
            <div className="text-lg font-semibold text-zinc-100">{formatMoney(customer.spent_cents)}</div>
            <div className="text-xs text-zinc-500">Spent</div>
          </div>
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
            <div className="text-lg font-semibold text-zinc-100">{active.length}</div>
            <div className="text-xs text-zinc-500">Access grants</div>
          </div>
        </div>

        <div>
          <Label>Access grants (digital content)</Label>
          {entitlements === null ? (
            <Skeleton className="h-16" />
          ) : entitlements.length === 0 ? (
            <p className="text-sm text-zinc-500">No access grants yet.</p>
          ) : (
            <div className="space-y-1.5">
              {entitlements.map((e) => (
                <div key={e.id} className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-sm">
                  <KeyRound className={`h-3.5 w-3.5 ${e.status === "active" ? "text-emerald-400" : "text-zinc-600"}`} />
                  <span className="min-w-0 flex-1 truncate text-zinc-200">{e.product_name}</span>
                  <Badge color={e.status === "active" ? "emerald" : "zinc"} size="sm">{e.status}</Badge>
                  <span className="text-xs text-zinc-600">{e.source}</span>
                  {e.status === "active" && (
                    <button onClick={() => void revoke(e)} className="rounded p-1 text-zinc-500 hover:text-rose-300" title="Revoke">
                      <XCircle className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {grantable.length > 0 && (
          <div>
            <Label htmlFor="grant">Grant access manually</Label>
            <div className="flex gap-2">
              <Select id="grant" value={grantId} onChange={(e) => setGrantId(e.target.value)} className="flex-1">
                <option value="">Choose a product…</option>
                {grantable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
              <Button onClick={grant} loading={busy} disabled={!grantId} leftIcon={<Plus className="h-4 w-4" />}>
                Grant
              </Button>
            </div>
          </div>
        )}
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}
